import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatBC } from "@/lib/format";
import { Toaster, toast } from "sonner";
import { Shield, AlertTriangle, CheckCircle2, X, ShieldCheck, RefreshCw, Users, ArrowDownCircle, ArrowUpCircle, ShoppingBag, Gauge, MessageCircle, Fingerprint, Eye, Image as ImageIcon, Radio, Video, Activity, UserCog, Inbox, Trash2, Package, Brain } from "lucide-react";
import AdminOperators from "@/pages/admin/AdminOperators";
import AdminStorefront from "@/pages/admin/AdminStorefront";
import AdminCap from "@/pages/admin/AdminCap";
import AdminChat from "@/pages/admin/AdminChat";
import AdminKyc from "@/pages/admin/AdminKyc";
import AdminWall from "@/pages/admin/AdminWall";
import AdminOnline from "@/pages/admin/AdminOnline";
import AdminAdVideos from "@/pages/admin/AdminAdVideos";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminInbox from "@/pages/admin/AdminInbox";
import AdminMembership from "@/pages/admin/AdminMembership";
import AdminIntelligence from "@/pages/admin/AdminIntelligence";
import AdminBusinessBrain from "@/pages/admin/AdminBusinessBrain";
import LaunchHealthPanel from "@/components/LaunchHealthPanel";
import AdminCashInChannels from "@/pages/admin/AdminCashInChannels";

function StatPill({ label, value, accent = "white", testid }) {
  const map = { white: "text-white", gold: "text-[#FBBF24]", crimson: "text-[#FCA5A5]", blue: "text-[#93C5FD]" };
  return (
    <div className="glass-card p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
      <div className={`font-mono-num text-2xl mt-1 ${map[accent]}`}>{value}</div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const isSuper = user?.role === "admin";
  const grants = new Set(user?.admin_permissions || []);
  const can = (scope) => isSuper || grants.has(scope);

  const [tab, setTab] = useState(null);
  const [stats, setStats] = useState(null);
  const [deps, setDeps] = useState([]);
  const [wds, setWds] = useState([]);
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(null);
  const [releaseFor, setReleaseFor] = useState(null);
  const [outgoingRef, setOutgoingRef] = useState("");
  const [viewingReceipt, setViewingReceipt] = useState(null);

  const openReceipt = async (id) => {
    try {
      const { data } = await api.get(`/deposits/${id}/receipt`);
      if (data.receipt_image) setViewingReceipt({ id, src: data.receipt_image });
      else toast.error("No receipt attached.");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const load = async () => {
    // Sub-admins may be missing individual permissions — swallow 403s so the
    // console still renders. The tabs they can't see just won't show data.
    const safe = (p) => api.get(p).then((r) => r.data).catch(() => null);
    const [s, d, w] = await Promise.all([
      can("cap") ? safe("/admin/stats") : Promise.resolve(null),
      can("deposits") ? safe("/admin/deposits?status=pending") : Promise.resolve([]),
      can("withdrawals") ? safe("/admin/withdrawals?status=pending") : Promise.resolve([]),
    ]);
    setStats(s);
    setDeps(d || []);
    setWds(w || []);
  };

  useEffect(() => {
    // Pick the first tab the current admin can see. Super-admins default to
    // deposits; sub-admins get the first scope they were granted.
    const order = ["deposits", "withdrawals", "operators", "storefront", "cap", "chat", "kyc", "wall", "online", "adstation", "analytics", "inbox"];
    if (tab === null) {
      const first = order.find((s) => can(s));
      setTab(first || (isSuper ? "team" : "deposits"));
    }
    load();
  }, [user?.id]);

  const runAudit = async () => {
    setBusy("audit");
    try {
      const { data } = await api.get("/admin/audit");
      setAudit(data);
      await load();
      if (data.discrepancy === 0 && data.chain_ok) toast.success("Audit passed. Ledger integrity intact.");
      else toast.error("Discrepancy detected. System auto-locked.");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  const approveDeposit = async (d) => {
    setBusy(d.id);
    try {
      await api.post(`/admin/deposits/${d.id}/approve`);
      toast.success(`Approved ₱${formatBC(d.amount)} for ${d.user_email}`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  const rejectDeposit = async (d) => {
    setBusy(d.id);
    try { await api.post(`/admin/deposits/${d.id}/reject`); toast.success("Deposit rejected"); await load(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(null); }
  };

  const confirmRelease = async () => {
    if (!releaseFor || !outgoingRef.trim()) return;
    setBusy(releaseFor.id);
    try {
      await api.post(`/admin/withdrawals/${releaseFor.id}/confirm-release`, { outgoing_reference: outgoingRef.trim() });
      toast.success(`Released ₱${formatBC(releaseFor.amount)} to ${releaseFor.user_email}`);
      setReleaseFor(null); setOutgoingRef("");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  const rejectWithdrawal = async (w) => {
    setBusy(w.id);
    try { await api.post(`/admin/withdrawals/${w.id}/reject`); toast.success("Withdrawal rejected & funds restored"); await load(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(null); }
  };

  const clearLockdown = async () => {
    setBusy("clear");
    try { await api.post("/admin/lockdown/clear"); toast.success("Lockdown cleared"); await load(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(null); }
  };

  const purgeTestPollution = async () => {
    // Two-step flow: dry-run first (shows the shape of the purge), then a
    // spelled-out confirm before the destructive apply=true call.
    setBusy("purge-preview");
    try {
      const { data } = await api.post("/admin/system/purge-test-pollution");
      const cascade = Object.entries(data.cascade || {}).map(([k, v]) => `  ${k}: ${v}`).join("\n");
      const msg =
        `PURGE TEST POLLUTION — DRY RUN\n\n` +
        `Users to delete: ${data.users_to_purge}\n` +
        (data.sample_emails.length ? `First few:\n  ${data.sample_emails.join("\n  ")}\n\n` : "\n") +
        `Cascade:\n${cascade || "  (nothing to cascade)"}\n\n` +
        `Whitelisted (kept):\n  ${data.whitelisted_kept.join("\n  ")}\n\n` +
        `Type YES to actually delete everything above. This is IRREVERSIBLE.`;
      if (data.users_to_purge === 0) {
        toast.success("No test pollution detected — database is already clean.");
        return;
      }
      const answer = window.prompt(msg, "");
      if (answer !== "YES") { toast.info("Purge cancelled."); return; }
      setBusy("purge-apply");
      const { data: result } = await api.post("/admin/system/purge-test-pollution?apply=true");
      const deleted = Object.entries(result.deleted || {}).map(([k, v]) => `${k}: ${v}`).join(", ");
      toast.success(`Purge complete. Removed → ${deleted || "nothing"}`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-7" data-testid="admin-root">
      <Toaster position="top-right" richColors />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#DC2626] font-bold flex items-center gap-2"><Shield size={12} /> Admin Operations</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Zenith Command Center</h1>
          <p className="text-white/55 mt-1 text-sm">Partners · Products · Approvals · Executive Intelligence</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {user?.role === "admin" && (
            <button
              onClick={purgeTestPollution}
              disabled={busy === "purge-preview" || busy === "purge-apply"}
              className="text-xs px-3 py-2 rounded-md border border-[#DC2626]/60 bg-[#DC2626]/10 hover:bg-[#DC2626]/25 text-[#FCA5A5] hover:text-white font-bold disabled:opacity-50 flex items-center gap-2"
              data-testid="purge-test-pollution-btn"
              title="Wipe test_*@example.com and *@zfd.example.com users + cascade. Admin-only. Dry-runs first."
            >
              <Trash2 size={13} /> {busy === "purge-apply" ? "Purging…" : "Purge Test Data"}
            </button>
          )}
        </div>
      </div>

      {/* Investment-era Reconciliation Audit banner + Reserve/Liability/
          Discrepancy stat blocks and LaunchHealthPanel have been retired in
          the CaaS pivot. Real operational health is now surfaced via the
          Executive Intelligence tab (KPI Health Board). */}

      <div className="glass-card p-1 inline-flex rounded-lg gap-1 flex-wrap" role="tablist">
        {isSuper && (
          <button onClick={() => setTab("intelligence")} className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest ${tab === "intelligence" ? "bg-gradient-to-r from-[#FBBF24] to-yellow-300 text-[#0F2042]" : "text-[#FBBF24] hover:bg-white/5"}`} data-testid="tab-intelligence">
            <Activity size={14} className="inline mr-1" /> Executive Intelligence
          </button>
        )}
        {isSuper && (
          <button onClick={() => setTab("brain")} className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest ${tab === "brain" ? "bg-gradient-to-r from-[#FBBF24] to-yellow-300 text-[#0F2042]" : "text-[#FBBF24] hover:bg-white/5"}`} data-testid="tab-brain">
            <Brain size={14} className="inline mr-1" /> Business Brain
          </button>
        )}
        {can("deposits") && (
          <button onClick={() => setTab("membership")} className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest ${tab === "membership" ? "bg-[#1E3A8A] text-white" : "text-white/60 hover:bg-white/5"}`} data-testid="tab-membership">
            <Package size={14} className="inline mr-1" /> Kit Approvals
          </button>
        )}
        {can("withdrawals") && (
          <button onClick={() => setTab("withdrawals")} className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest ${tab === "withdrawals" ? "bg-[#1E3A8A] text-white" : "text-white/60 hover:bg-white/5"}`} data-testid="tab-withdrawals">
            <ArrowUpCircle size={14} className="inline mr-1" /> Withdrawals ({wds.length})
          </button>
        )}
        {can("operators") && (
          <button onClick={() => setTab("operators")} className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest ${tab === "operators" ? "bg-[#1E3A8A] text-white" : "text-white/60 hover:bg-white/5"}`} data-testid="tab-operators">
            <Users size={14} className="inline mr-1" /> Partners
          </button>
        )}
        {can("storefront") && (
          <button onClick={() => setTab("storefront")} className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest ${tab === "storefront" ? "bg-[#1E3A8A] text-white" : "text-white/60 hover:bg-white/5"}`} data-testid="tab-storefront">
            <ShoppingBag size={14} className="inline mr-1" /> Storefront
          </button>
        )}
        {can("chat") && (
          <button onClick={() => setTab("chat")} className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest ${tab === "chat" ? "bg-[#1E3A8A] text-white" : "text-white/60 hover:bg-white/5"}`} data-testid="tab-chat">
            <MessageCircle size={14} className="inline mr-1" /> Support Chat
          </button>
        )}
        {can("kyc") && (
          <button onClick={() => setTab("kyc")} className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest ${tab === "kyc" ? "bg-[#1E3A8A] text-white" : "text-white/60 hover:bg-white/5"}`} data-testid="tab-kyc">
            <Fingerprint size={14} className="inline mr-1" /> KYC
          </button>
        )}
        {can("wall") && (
          <button onClick={() => setTab("wall")} className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest ${tab === "wall" ? "bg-[#1E3A8A] text-white" : "text-white/60 hover:bg-white/5"}`} data-testid="tab-wall">
            <Radio size={14} className="inline mr-1" /> Live Activity
          </button>
        )}
        {can("online") && (
          <button onClick={() => setTab("online")} className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest ${tab === "online" ? "bg-[#1E3A8A] text-white" : "text-white/60 hover:bg-white/5"}`} data-testid="tab-online">
            <Eye size={14} className="inline mr-1" /> Live Partners
          </button>
        )}
        {can("adstation") && (
          <button onClick={() => setTab("adstation")} className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest ${tab === "adstation" ? "bg-[#1E3A8A] text-white" : "text-white/60 hover:bg-white/5"}`} data-testid="tab-adstation">
            <Video size={14} className="inline mr-1" /> Marketing Center
          </button>
        )}
        {can("analytics") && (
          <button onClick={() => setTab("analytics")} className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest ${tab === "analytics" ? "bg-[#1E3A8A] text-white" : "text-white/60 hover:bg-white/5"}`} data-testid="tab-analytics">
            <Activity size={14} className="inline mr-1" /> Analytics
          </button>
        )}
        {can("inbox") && (
          <button onClick={() => setTab("inbox")} className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest ${tab === "inbox" ? "bg-[#1E3A8A] text-white" : "text-white/60 hover:bg-white/5"}`} data-testid="tab-inbox">
            <Inbox size={14} className="inline mr-1" /> Support Inbox
          </button>
        )}
        {isSuper && (
          <button onClick={() => setTab("team")} className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-widest ${tab === "team" ? "bg-[#DC2626] text-white" : "text-[#FCA5A5] hover:bg-white/5"}`} data-testid="tab-team">
            <UserCog size={14} className="inline mr-1" /> Team
          </button>
        )}
      </div>

      {tab === "operators" && <AdminOperators />}
      {tab === "storefront" && <AdminStorefront />}
      {tab === "chat" && <AdminChat />}
      {tab === "kyc" && <AdminKyc />}
      {tab === "wall" && <AdminWall />}
      {tab === "online" && <AdminOnline />}
      {tab === "analytics" && <AdminAnalytics />}
      {tab === "adstation" && <AdminAdVideos />}
      {tab === "inbox" && <AdminInbox />}
      {tab === "membership" && <AdminMembership />}
      {tab === "intelligence" && <AdminIntelligence />}
      {tab === "brain" && isSuper && <AdminBusinessBrain />}
      {tab === "team" && isSuper && <AdminTeam />}

      {/* The legacy `deposits` (Cash-Ins) tab table has been retired in the
          CaaS pivot — kit purchases now flow through the `membership` tab
          (Kit Approvals). Withdrawals below still power the commission
          payout queue. */}

      {tab === "withdrawals" && (
        <div className="glass-card p-4" data-testid="admin-withdrawals-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[#FBBF24]">
                  <th className="text-left p-2">Submitted</th>
                  <th className="text-left">User</th>
                  <th className="text-left">Channel</th>
                  <th className="text-left">Account</th>
                  <th className="text-right">Gross</th>
                  <th className="text-right">Fee (5%)</th>
                  <th className="text-right">Net Payout</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {wds.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-white/40 py-6">Queue clear.</td></tr>
                )}
                {wds.map((w) => {
                  const wFee = w.fee ?? +(w.amount * 0.05).toFixed(2);
                  const wNet = w.net_payout ?? +(w.amount - wFee).toFixed(2);
                  return (
                  <tr key={w.id} className="border-t border-white/5 hover:bg-white/[0.04]">
                    <td className="p-2 text-xs text-white/70">{new Date(w.submitted_at).toLocaleString()}</td>
                    <td className="text-xs">{w.user_name}<br/><span className="text-white/40">{w.user_email}</span></td>
                    <td className="uppercase text-xs">{w.channel}</td>
                    <td className="text-xs">{w.account_name}<br/><span className="text-white/40">{w.account_number}</span></td>
                    <td className="text-right font-mono-num text-white/80">₱{formatBC(w.amount)}</td>
                    <td className="text-right font-mono-num text-[#FCA5A5]">− ₱{formatBC(wFee)}</td>
                    <td className="text-right font-mono-num text-[#FBBF24]">₱{formatBC(wNet)}</td>
                    <td className="text-right space-x-1">
                      <button onClick={() => { setReleaseFor(w); setOutgoingRef(""); }} className="bg-emerald-600 hover:bg-emerald-500 px-2 py-1 rounded text-xs font-bold" data-testid={`release-wd-${w.id}`}>Confirm Release</button>
                      <button disabled={busy === w.id} onClick={() => rejectWithdrawal(w)} className="bg-[#DC2626] hover:bg-red-700 px-2 py-1 rounded text-xs font-bold disabled:opacity-40" data-testid={`reject-wd-${w.id}`}>Reject</button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {releaseFor && (() => {
        const rFee = releaseFor.fee ?? +(releaseFor.amount * 0.05).toFixed(2);
        const rNet = releaseFor.net_payout ?? +(releaseFor.amount - rFee).toFixed(2);
        return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-5" data-testid="release-modal">
          <div className="glass-card gold-border-glow p-7 max-w-md w-full">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">Confirm Release</div>
            <h3 className="font-display text-2xl font-bold mt-1">Log outgoing fiat reference</h3>
            <div className="text-xs text-white/60 mt-2">
              Releasing to <strong>{releaseFor.user_name}</strong> via <span className="uppercase">{releaseFor.channel}</span> at <span className="font-mono-num">{releaseFor.account_number}</span>.
            </div>

            <div className="mt-4 bg-black/40 border border-[#FBBF24]/30 rounded-md p-3 space-y-1.5 text-xs" data-testid="release-fee-breakdown">
              <div className="flex justify-between text-white/65">
                <span>Gross debited from user</span>
                <span className="font-mono-num">₱{formatBC(releaseFor.amount)}</span>
              </div>
              <div className="flex justify-between text-[#FCA5A5]">
                <span>System Maintenance Fee (5%)</span>
                <span className="font-mono-num">− ₱{formatBC(rFee)}</span>
              </div>
              <div className="flex justify-between pt-1.5 border-t border-white/10">
                <span className="text-[#FBBF24] uppercase tracking-wider text-[10px] font-bold">Transfer this amount</span>
                <span className="font-mono-num text-[#FBBF24] text-lg" data-testid="release-net-amount">₱{formatBC(rNet)}</span>
              </div>
            </div>

            <div className="mt-4">
              <label className="zfd-label">Outgoing Transaction Reference</label>
              <input value={outgoingRef} onChange={(e) => setOutgoingRef(e.target.value)} className="zfd-input" placeholder="e.g. GC-78294120" data-testid="outgoing-ref-input" />
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setReleaseFor(null)} className="flex-1 bg-white/5 hover:bg-white/10 py-2.5 rounded-md font-semibold text-sm" data-testid="cancel-release">Cancel</button>
              <button disabled={!outgoingRef.trim()} onClick={confirmRelease} className="flex-1 btn-deploy disabled:opacity-50" data-testid="confirm-release-submit">Confirm Release</button>
            </div>
          </div>
        </div>
        );
      })()}

      {viewingReceipt && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setViewingReceipt(null)}
          data-testid="admin-receipt-modal"
        >
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <button className="absolute -top-10 right-0 text-white/70 hover:text-white" onClick={() => setViewingReceipt(null)} data-testid="admin-receipt-close">
              <X size={22} />
            </button>
            <img src={viewingReceipt.src} alt="deposit receipt" className="w-full max-h-[85vh] object-contain rounded-lg border border-white/10" />
          </div>
        </div>
      )}
    </div>
  );
}
